import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { GlassCard } from '@/components/ui/layout/card';
import { CardHeader, CardContent, CardTitle } from '@/components/ui/layout/card';
import { Input } from '@/components/ui/form/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Search, ChevronRight, Clock, AlertCircle, CheckCircle2, Filter, X } from 'lucide-react';
import { useTraceList, useTraceDetail } from '@/hooks/useTraces';
import type { TraceSession, TraceSpan, TraceStatus } from '@/lib/api/internalTracing';

const STATUS_STYLES: Record<TraceStatus, { dot: string; text: string; label: string }> = {
  ok: { dot: 'bg-green-500', text: 'text-green-500', label: 'OK' },
  error: { dot: 'bg-red-500', text: 'text-red-500', label: 'Error' },
  timeout: { dot: 'bg-amber-500', text: 'text-amber-500', label: 'Timeout' },
};

function StatusDot({ status }: { status: TraceStatus }) {
  return <div className={cn('h-2 w-2 rounded-full shrink-0', STATUS_STYLES[status].dot)} />;
}

function LatencyBar({ latencyMs, maxMs }: { latencyMs: number; maxMs: number }) {
  const pct = Math.min((latencyMs / maxMs) * 100, 100);
  const color = latencyMs > 2000 ? 'bg-red-500/70' : latencyMs > 500 ? 'bg-amber-500/70' : 'bg-green-500/50';
  return (
    <div className="h-1.5 w-full rounded-full bg-muted/40 overflow-hidden">
      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
    </div>
  );
}

function SpanRow({ span, maxMs, depth = 0 }: { span: TraceSpan & { children?: TraceSpan[] }; maxMs: number; depth?: number }) {
  const [open, setOpen] = useState(false);
  const hasChildren = span.children && span.children.length > 0;
  const meta = span.meta_json ?? {};
  const inputData = meta?.input;
  const outputData = meta?.output;
  const warnings = Array.isArray(meta?.warnings) ? meta.warnings : [];
  const metaPairs = Object.entries(meta).filter(([k]) => !['input', 'output', 'warnings'].includes(k));

  return (
    <>
      <button
        onClick={() => hasChildren && setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-3 py-2 px-3 rounded-md text-left transition-colors',
          'hover:bg-muted/30',
          hasChildren && 'cursor-pointer',
        )}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        {hasChildren ? (
          <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform shrink-0', open && 'rotate-90')} />
        ) : (
          <div className="w-3" />
        )}
        <StatusDot status={span.status} />
        <span className="font-mono text-xs text-foreground truncate flex-1">{span.span_name}</span>
        <span className="font-mono text-[11px] text-muted-foreground tabular-nums shrink-0 w-16 text-right">
          {span.latency_ms}ms
        </span>
        <div className="w-24 shrink-0">
          <LatencyBar latencyMs={span.latency_ms} maxMs={maxMs} />
        </div>
      </button>
      {open && span.children?.map((child) => (
        <SpanRow key={child.id} span={child as TraceSpan & { children?: TraceSpan[] }} maxMs={maxMs} depth={depth + 1} />
      ))}
      {open && span.error && (
        <div className="ml-12 px-3 py-1.5 text-xs font-mono text-red-400 bg-red-500/10 rounded border border-red-500/20 mb-1" style={{ marginLeft: `${32 + depth * 20}px` }}>
          {span.error}
        </div>
      )}
      {open && warnings.length > 0 && (
        <div className="px-3 py-2 text-[11px] font-mono text-amber-300 bg-amber-500/10 rounded border border-amber-500/20 mb-1" style={{ marginLeft: `${32 + depth * 20}px` }}>
          <div className="uppercase tracking-wider text-[10px] mb-1">warnings ({warnings.length})</div>
          {warnings.map((w, idx) => (
            <div key={idx} className="whitespace-pre-wrap break-words">{typeof w === 'string' ? w : JSON.stringify(w)}</div>
          ))}
        </div>
      )}
      {open && inputData !== undefined && (
        <div className="px-3 py-2 text-[11px] font-mono text-sky-300 bg-sky-500/10 rounded border border-sky-500/20 mb-1" style={{ marginLeft: `${32 + depth * 20}px` }}>
          <div className="uppercase tracking-wider text-[10px] mb-1">input</div>
          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(inputData, null, 2)}</pre>
        </div>
      )}
      {open && outputData !== undefined && (
        <div className="px-3 py-2 text-[11px] font-mono text-emerald-300 bg-emerald-500/10 rounded border border-emerald-500/20 mb-1" style={{ marginLeft: `${32 + depth * 20}px` }}>
          <div className="uppercase tracking-wider text-[10px] mb-1">output</div>
          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(outputData, null, 2)}</pre>
        </div>
      )}
      {open && metaPairs.length > 0 && (
        <div className="px-3 py-1.5 text-[11px] font-mono text-muted-foreground bg-muted/20 rounded mb-1" style={{ marginLeft: `${32 + depth * 20}px` }}>
          {metaPairs.map(([k, v]) => (
            <span key={k} className="mr-3">{k}=<span className="text-foreground">{String(v)}</span></span>
          ))}
        </div>
      )}
    </>
  );
}

function buildSpanTree(spans: TraceSpan[]): (TraceSpan & { children?: TraceSpan[] })[] {
  const map = new Map<string, TraceSpan & { children?: TraceSpan[] }>();
  const roots: (TraceSpan & { children?: TraceSpan[] })[] = [];
  spans.forEach((s) => map.set(s.id, { ...s, children: [] }));
  spans.forEach((s) => {
    const node = map.get(s.id)!;
    if (s.parent_span_id && map.has(s.parent_span_id)) {
      map.get(s.parent_span_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function TraceDetailPanel({ traceId, onClose }: { traceId: string; onClose: () => void }) {
  const { data: detail, isLoading } = useTraceDetail(traceId, true);

  if (isLoading) return <div className="p-6 text-muted-foreground text-sm">Loading trace…</div>;
  if (!detail) return <div className="p-6 text-muted-foreground text-sm">Trace not found.</div>;

  const { session, spans } = detail;
  const tree = buildSpanTree(spans);
  const maxMs = Math.max(...spans.map((s) => s.latency_ms), 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <StatusDot status={session.status} />
            <h3 className="font-mono text-sm font-medium text-foreground">{session.trace_id.slice(0, 16)}…</h3>
          </div>
          <p className="text-xs text-muted-foreground font-mono">{session.route} · {session.total_latency_ms}ms · {spans.length} spans</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
        <div className="text-muted-foreground">user_id</div>
        <div className="text-foreground truncate">{session.user_id}</div>
        <div className="text-muted-foreground">conversation_id</div>
        <div className="text-foreground truncate">{session.conversation_id ?? '—'}</div>
        <div className="text-muted-foreground">message_id</div>
        <div className="text-foreground truncate">{session.message_id ?? '—'}</div>
        <div className="text-muted-foreground">started_at</div>
        <div className="text-foreground">{new Date(session.started_at).toLocaleString()}</div>
        {session.root_error && (
          <>
            <div className="text-red-400">root_error</div>
            <div className="text-red-400">{session.root_error}</div>
          </>
        )}
      </div>

      {/* Span Waterfall */}
      <div className="border border-border/50 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-muted/20 border-b border-border/50 flex items-center justify-between">
          <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Span Waterfall</span>
          <span className="text-[11px] font-mono text-muted-foreground">max {maxMs}ms</span>
        </div>
        <div className="divide-y divide-border/30">
          {tree.map((span) => (
            <SpanRow key={span.id} span={span} maxMs={maxMs} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Mock Data ───────────────────────────────────────────────
const MOCK_TRACES: TraceSession[] = [
  {
    trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4',
    user_id: 'usr_demo_001',
    conversation_id: 'c_abc123',
    message_id: 'msg_001',
    route: '/coach/chat',
    started_at: new Date(Date.now() - 45000).toISOString(),
    ended_at: new Date(Date.now() - 43200).toISOString(),
    status: 'ok',
    root_error: null,
    total_latency_ms: 1823,
    span_count: 7,
  },
  {
    trace_id: 'trc_f7e8d9c0b1a2f7e8d9c0',
    user_id: 'usr_demo_002',
    conversation_id: 'c_def456',
    message_id: 'msg_002',
    route: '/coach/chat',
    started_at: new Date(Date.now() - 120000).toISOString(),
    ended_at: new Date(Date.now() - 116500).toISOString(),
    status: 'error',
    root_error: 'LLM timeout after 30s',
    total_latency_ms: 3482,
    span_count: 5,
  },
  {
    trace_id: 'trc_1234567890abcdef1234',
    user_id: 'usr_demo_001',
    conversation_id: null,
    message_id: null,
    route: '/calendar/today',
    started_at: new Date(Date.now() - 300000).toISOString(),
    ended_at: new Date(Date.now() - 299700).toISOString(),
    status: 'ok',
    root_error: null,
    total_latency_ms: 312,
    span_count: 3,
  },
  {
    trace_id: 'trc_abcdef1234567890abcd',
    user_id: 'usr_demo_003',
    conversation_id: 'c_ghi789',
    message_id: 'msg_003',
    route: '/coach/chat',
    started_at: new Date(Date.now() - 600000).toISOString(),
    ended_at: new Date(Date.now() - 597000).toISOString(),
    status: 'timeout',
    root_error: 'Request exceeded 30s deadline',
    total_latency_ms: 30012,
    span_count: 4,
  },
];

const MOCK_SPANS: TraceSpan[] = [
  { id: 'sp1', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: null, span_name: 'coach_chat_handler', started_at: '', ended_at: '', latency_ms: 1823, status: 'ok', error: null, meta_json: { model: 'gpt-4o', route: '/coach/chat' } },
  { id: 'sp2', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp1', span_name: 'history_load', started_at: '', ended_at: '', latency_ms: 89, status: 'ok', error: null, meta_json: { messages: 24 } },
  { id: 'sp3', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp1', span_name: 'router_llm', started_at: '', ended_at: '', latency_ms: 420, status: 'ok', error: null, meta_json: { intent: 'modify', confidence: 0.94 } },
  { id: 'sp4', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp1', span_name: 'decision_build', started_at: '', ended_at: '', latency_ms: 680, status: 'ok', error: null, meta_json: { tool: 'modify_session', confirmed: true } },
  { id: 'sp5', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp4', span_name: 'openai_completion', started_at: '', ended_at: '', latency_ms: 540, status: 'ok', error: null, meta_json: { model: 'gpt-4o', tokens_in: 2100, tokens_out: 380 } },
  { id: 'sp6', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp1', span_name: 'save_sessions', started_at: '', ended_at: '', latency_ms: 45, status: 'ok', error: null, meta_json: { rows: 2 } },
  { id: 'sp7', trace_id: 'trc_a1b2c3d4e5f6a1b2c3d4', parent_span_id: 'sp1', span_name: 'redis_cache_write', started_at: '', ended_at: '', latency_ms: 12, status: 'ok', error: null, meta_json: null },
];

// ─── Main Page ───────────────────────────────────────────────
export default function QueryTracing() {
  const [filters, setFilters] = useState({
    user_id: '',
    conversation_id: '',
    trace_id: '',
    status: '',
    route: '',
    from: '',
    to: '',
    include_internal: false,
    detailed_only: true,
  });
  const [activeFilters, setActiveFilters] = useState(filters);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const toIso = (value: string) => (value ? new Date(value).toISOString() : undefined);
  const setPresetRange = (minutes: number) => {
    const now = new Date();
    const start = new Date(now.getTime() - minutes * 60 * 1000);
    setFilters((f) => ({
      ...f,
      from: start.toISOString().slice(0, 16),
      to: now.toISOString().slice(0, 16),
    }));
  };

  const { data: listData, isLoading } = useTraceList(
    {
      user_id: activeFilters.user_id || undefined,
      conversation_id: activeFilters.conversation_id || undefined,
      trace_id: activeFilters.trace_id || undefined,
      status: activeFilters.status || undefined,
      route: activeFilters.route || undefined,
      from: toIso(activeFilters.from),
      to: toIso(activeFilters.to),
      include_internal: activeFilters.include_internal,
      min_span_count: activeFilters.detailed_only ? 2 : undefined,
      limit: 50,
    },
    true,
  );

  const traces = listData?.traces ?? [];
  const total = listData?.total ?? traces.length;
  const errorCount = traces.filter((t) => t.status !== 'ok').length;
  const errorRate = traces.length ? Math.round((errorCount / traces.length) * 100) : 0;
  const latencies = traces.map((t) => t.total_latency_ms).sort((a, b) => a - b);
  const avgLatency = traces.length ? Math.round(latencies.reduce((sum, v) => sum + v, 0) / traces.length) : 0;
  const p95Latency = latencies.length ? latencies[Math.max(0, Math.floor(latencies.length * 0.95) - 1)] : 0;

  const handleSearch = () => setActiveFilters({ ...filters });

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-primary">Query Tracing</h1>
            <p className="text-muted-foreground mt-1">Per-request trace explorer with span waterfall</p>
          </div>
          <Badge variant="outline" className="self-start sm:self-auto text-muted-foreground border-muted-foreground/30">
            Admin · Read-only
          </Badge>
        </div>

        {/* Filters */}
        <GlassCard>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Search Filters</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
              <Input
                placeholder="user_id"
                value={filters.user_id}
                onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <Input
                placeholder="conversation_id"
                value={filters.conversation_id}
                onChange={(e) => setFilters((f) => ({ ...f, conversation_id: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <Input
                placeholder="trace_id"
                value={filters.trace_id}
                onChange={(e) => setFilters((f) => ({ ...f, trace_id: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <Input
                placeholder="route (e.g. /coach/chat)"
                value={filters.route}
                onChange={(e) => setFilters((f) => ({ ...f, route: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <Input
                type="datetime-local"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <Input
                type="datetime-local"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="font-mono text-xs h-8"
              />
              <div className="flex gap-2">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs font-mono text-foreground"
                >
                  <option value="">All statuses</option>
                  <option value="ok">OK</option>
                  <option value="error">Error</option>
                  <option value="timeout">Timeout</option>
                </select>
                <Button size="sm" onClick={handleSearch} className="h-8 px-3">
                  <Search className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPresetRange(15)}>Last 15m</Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPresetRange(60)}>Last 1h</Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setPresetRange(24 * 60)}>Last 24h</Button>
              <label className="ml-2 inline-flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <input
                  type="checkbox"
                  checked={!filters.include_internal}
                  onChange={(e) => setFilters((f) => ({ ...f, include_internal: !e.target.checked }))}
                />
                hide internal ops noise
              </label>
              <label className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <input
                  type="checkbox"
                  checked={filters.detailed_only}
                  onChange={(e) => setFilters((f) => ({ ...f, detailed_only: e.target.checked }))}
                />
                detailed only (2+ spans)
              </label>
            </div>
          </CardContent>
        </GlassCard>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <GlassCard>
            <CardContent className="pt-3 pb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Total Traces</div>
              <div className="text-xl font-semibold text-foreground mt-1 tabular-nums">{total}</div>
            </CardContent>
          </GlassCard>
          <GlassCard>
            <CardContent className="pt-3 pb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Error Rate</div>
              <div className={cn('text-xl font-semibold mt-1 tabular-nums', errorRate > 10 ? 'text-red-400' : 'text-foreground')}>
                {errorRate}%
              </div>
            </CardContent>
          </GlassCard>
          <GlassCard>
            <CardContent className="pt-3 pb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Avg Latency</div>
              <div className="text-xl font-semibold text-foreground mt-1 tabular-nums">{avgLatency}ms</div>
            </CardContent>
          </GlassCard>
          <GlassCard>
            <CardContent className="pt-3 pb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">P95 Latency</div>
              <div className="text-xl font-semibold text-foreground mt-1 tabular-nums">{p95Latency}ms</div>
            </CardContent>
          </GlassCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Trace List */}
          <div className={cn('space-y-2', selectedTraceId ? 'lg:col-span-2' : 'lg:col-span-5')}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                {traces.length} trace{traces.length !== 1 ? 's' : ''}
              </span>
              {isLoading && <span className="text-xs text-muted-foreground animate-pulse">Loading…</span>}
            </div>
            <div className="space-y-1.5">
              {traces.map((trace) => (
                <button
                  key={trace.trace_id}
                  onClick={() => setSelectedTraceId(trace.trace_id === selectedTraceId ? null : trace.trace_id)}
                  className={cn(
                    'w-full text-left rounded-lg border transition-all p-3',
                    trace.trace_id === selectedTraceId
                      ? 'border-accent bg-accent/10'
                      : 'border-border/50 bg-card hover:bg-muted/30 hover:border-border',
                  )}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <StatusDot status={trace.status} />
                    <span className="font-mono text-xs text-foreground truncate flex-1">
                      {trace.trace_id.slice(0, 20)}…
                    </span>
                    <span className={cn('text-[11px] font-mono font-medium tabular-nums', STATUS_STYLES[trace.status].text)}>
                      {trace.total_latency_ms}ms
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
                    <span className="truncate">{trace.route}</span>
                    <span className="shrink-0">{trace.span_count} spans</span>
                    <span className="shrink-0 ml-auto">
                      {new Date(trace.started_at).toLocaleTimeString()}
                    </span>
                  </div>
                  {trace.root_error && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-red-400 font-mono">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      <span className="truncate">{trace.root_error}</span>
                    </div>
                  )}
                </button>
              ))}
              {traces.length === 0 && !isLoading && (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  No traces found. Adjust filters or wait for incoming requests.
                </div>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          {selectedTraceId && (
            <div className="lg:col-span-3">
              <GlassCard>
                <CardContent className="pt-4 pb-4">
                  <TraceDetailPanel traceId={selectedTraceId} onClose={() => setSelectedTraceId(null)} />
                </CardContent>
              </GlassCard>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
